import React from 'react';
import { Row, Col, Form, Button, Table, Card, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCreditCard, faSave, faEdit } from '@fortawesome/free-solid-svg-icons';
import { SortableHeader } from '../../../ui/SortableHeader/SortableHeader';
import { Helpers } from '../../../../utils/helpers';
import { usePayments } from './usePayments';
import { PaymentStatus } from '../../../../utils/constants/enums';
import styles from './PaymentSection.module.css';

// Helper function to format payment status with appropriate styling
const getPaymentStatusDisplay = (status: PaymentStatus): { text: string; variant: string } => {
  switch (status) {
    case PaymentStatus.Completed:
      return { text: 'Completed', variant: 'success' };
    case PaymentStatus.Pending:
      return { text: 'Pending', variant: 'warning' };
    case PaymentStatus.Refunded:
      return { text: 'Refunded', variant: 'info' };
    case PaymentStatus.Failed:
      return { text: 'Failed', variant: 'danger' };
    case PaymentStatus.Cancelled:
      return { text: 'Cancelled', variant: 'secondary' };
    default:
      return { text: 'Unknown', variant: 'secondary' };
  }
};

interface PaymentSectionProps {
  paymentsHook: ReturnType<typeof usePayments>;
}

export const PaymentSection: React.FC<PaymentSectionProps> = ({ paymentsHook }) => {
  const {
    loadingPayments,
    savingPayments,
    paymentFormData,
    paymentFormMode,
    selectedPaymentRow,
    paymentYearFilter,
    paymentSortField,
    paymentSortDirection,
    clubMemberId,
    fieldErrors,
    paymentMethodOptions,
    loadingPaymentMethods,
    updatePaymentFormData,
    savePayment,
    clearPaymentForm,
    enableAddPaymentMode,
    enableEditPaymentMode,
    setPaymentYearFilter,
    handlePaymentSort,
    getPaymentAvailableYears,
    getFilteredAndSortedPayments
  } = paymentsHook;

  const isNew = Helpers.isNullOrWhiteSpace(clubMemberId) || clubMemberId === 'new';

  if (isNew) {
    return (
      <div className={styles.placeholderContent}>
        <FontAwesomeIcon icon={faCreditCard} className={styles.placeholderIcon} />
        <div className={styles.placeholderText}>Payment tracking available after save</div>
        <div className={styles.placeholderSubtext}>
          Save the member information first to start tracking payments
        </div>
      </div>
    );
  }

  const availableYears = getPaymentAvailableYears();
  const filteredAndSortedPayments = getFilteredAndSortedPayments();

  return (
    <>
      {/* Add Payment Form */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={paymentFormMode === 'edit' ? faEdit : faPlus} className="me-2" />
            {paymentFormMode === 'edit' ? 'Edit Payment' : 'Add Payment'}
          </h5>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={paymentFormData.paymentId}
                    disabled={true}
                    placeholder={paymentFormMode === 'add' ? 'Auto' : ''}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={paymentFormData.paymentDateUtc}
                    onChange={(e) => updatePaymentFormData('paymentDateUtc', e.target.value)}
                    isInvalid={fieldErrors.includes('payments:paymentDateUtc')}
                    disabled={paymentFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Payment date is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Year</Form.Label>
                  <Form.Control
                    type="number"
                    min="1900"
                    max="2100"
                    value={paymentFormData.billingYear}
                    onChange={(e) => updatePaymentFormData('billingYear', e.target.value)}
                    isInvalid={fieldErrors.includes('payments:billingYear')}
                    placeholder="YYYY"
                    disabled={paymentFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Enter a valid year (1900-2100).
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select
                    value={paymentFormData.paymentMethodId}
                    onChange={(e) => updatePaymentFormData('paymentMethodId', e.target.value)}
                    isInvalid={fieldErrors.includes('payments:paymentMethodId')}
                    disabled={paymentFormMode === 'disabled'}>
                    <option value="">Select Method</option>
                    {paymentMethodOptions.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.description || method.codeValue}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingPaymentMethods && <small className="text-muted">Loading...</small>}
                  <Form.Control.Feedback type="invalid">
                    Payment method is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentFormData.amount}
                    onChange={(e) => updatePaymentFormData('amount', e.target.value)}
                    isInvalid={fieldErrors.includes('payments:amount')}
                    placeholder="0.00"
                    disabled={paymentFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Amount must be a valid non-negative number.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Order Number</Form.Label>
                  <Form.Control type="text" value="" disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transaction ID</Form.Label>
                  <Form.Control type="text" value="" disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Order ID</Form.Label>
                  <Form.Control type="text" value="" disabled />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={paymentFormData.notes}
                    onChange={(e) => updatePaymentFormData('notes', e.target.value)}
                    placeholder="Payment notes..."
                    maxLength={500}
                    disabled={paymentFormMode === 'disabled'}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Button
                  variant="success"
                  onClick={savePayment}
                  disabled={paymentFormMode === 'disabled' || savingPayments}
                  className="me-2">
                  {savingPayments && <Spinner size="sm" className="me-2" />}
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  Save
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={clearPaymentForm}
                  className="me-2"
                  disabled={savingPayments}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={enableAddPaymentMode}
                  disabled={paymentFormMode === 'edit' || paymentFormMode === 'add'}
                  className="me-2">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Payment History Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faCreditCard} className="me-2" />
            Payment History
          </h5>
        </Card.Header>
        <Card.Body>
          {loadingPayments ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Loading payments...</div>
            </div>
          ) : filteredAndSortedPayments.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FontAwesomeIcon icon={faCreditCard} size="2x" className="mb-3" />
              <div>
                {paymentYearFilter === 'All'
                  ? 'No payments recorded yet'
                  : `No payments recorded for ${paymentYearFilter}`}
              </div>
              <div className="small">Add payments using the form above</div>
            </div>
          ) : (
            <>
              {/* Year Filter Row */}
              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Group className="mb-0">
                    <Form.Label className="me-2 mb-0">Show Payments for Year:</Form.Label>
                    <Form.Select
                      size="sm"
                      value={paymentYearFilter}
                      onChange={(e) => setPaymentYearFilter(e.target.value)}
                      style={{ width: '120px', display: 'inline-block' }}>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <SortableHeader
                        text="Status"
                        sortField="paymentStatus"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'paymentStatus'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Date"
                        sortField="paymentDateUtc"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'paymentDateUtc'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Year"
                        sortField="billingYear"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'billingYear'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Method"
                        sortField="paymentMethodId"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'paymentMethodId'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Amount"
                        sortField="amount"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'amount'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Order #"
                        sortField="orderNumber"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'orderNumber'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Trans. ID"
                        sortField="transactionId"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'transactionId'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Order ID"
                        sortField="orderId"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'orderId'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Notes"
                        sortField="notes"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'notes'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Created By"
                        sortField="createdBy"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'createdBy'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Created"
                        sortField="dateCreatedUtc"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'dateCreatedUtc'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Modified By"
                        sortField="lastModifiedBy"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'lastModifiedBy'}
                        onClick={handlePaymentSort}
                      />
                      <SortableHeader
                        text="Modified"
                        sortField="lastModifiedDateUtc"
                        sortDirection={paymentSortDirection}
                        active={paymentSortField === 'lastModifiedDateUtc'}
                        onClick={handlePaymentSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedPayments.map((payment, index) => {
                      const statusDisplay = getPaymentStatusDisplay(payment.paymentStatus);
                      return (
                        <tr
                          key={payment.id}
                          onClick={() => enableEditPaymentMode(payment, index)}
                          className={`${styles.clickableRow} ${
                            selectedPaymentRow === index ? styles.selectedRow : ''
                          }`}
                          style={{ cursor: 'pointer' }}>
                          <td>
                            <Badge bg={statusDisplay.variant}>{statusDisplay.text}</Badge>
                          </td>
                          <td>{Helpers.toShortDateFromUtc(payment.paymentDateUtc)}</td>
                          <td>{payment.billingYear}</td>
                          <td>
                            {payment.paymentMethod?.description ||
                              payment.paymentMethod?.codeValue ||
                              'Unknown'}
                          </td>
                          <td>${payment.amount.toFixed(2)}</td>
                          <td>{payment.orderNumber || '-'}</td>
                          <td>{payment.transactionId || '-'}</td>
                          <td>{payment.orderId || '-'}</td>
                          <td>{payment.notes || '-'}</td>
                          <td>{payment.createdBy}</td>
                          <td>{Helpers.toShortDateFromUtc(payment.dateCreatedUtc)}</td>
                          <td>{payment.lastModifiedBy}</td>
                          <td>{Helpers.toShortDateFromUtc(payment.lastModifiedDateUtc)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                {filteredAndSortedPayments.length > 0 && (
                  <div className="mt-3 text-end">
                    <strong>
                      Total Paid: $
                      {filteredAndSortedPayments
                        .filter((payment) => payment.paymentStatus === PaymentStatus.Completed)
                        .reduce((sum, payment) => sum + payment.amount, 0)
                        .toFixed(2)}
                    </strong>
                  </div>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
